<template>
	<div class="component article-share">
		<button
			:id="shareToggleId"
			type="button"
			class="article-share__trigger"
			:class="{ 'article-share__trigger--navbar': navBarItem }"
			aria-label="Artikel delen"
		>
			<span v-show="label" class="article-share__label">{{ label }}</span>
			<span class="article-share__trigger-icon" aria-hidden="true">
				<Share2 :size="18" :stroke-width="2" />
			</span>
		</button>
		<div
			class="article-share__dropdown"
			:uk-dropdown="`toggle: #${shareToggleId}; mode: click; pos: bottom-right; offset: 10; animation: uk-animation-slide-top-small`"
		>
			<ul class="article-share__list">
				<li><a class="article-share__link" href="javascript:void(0)" @click="shareOnWhatsApp"><span class="article-share__icon" uk-icon="icon: whatsapp"></span><span>WhatsApp</span></a></li>
				<li><a class="article-share__link" href="javascript:void(0)" @click="shareOnFacebook"><span class="article-share__icon" uk-icon="icon: facebook"></span><span>Facebook</span></a></li>
				<li><a class="article-share__link" href="javascript:void(0)" @click="shareOnTwitter"><span class="article-share__icon" uk-icon="icon: twitter"></span><span>X</span></a></li>
				<li><a class="article-share__link" href="javascript:void(0)" @click="shareOnLinkedIn"><span class="article-share__icon" uk-icon="icon: linkedin"></span><span>LinkedIn</span></a></li>
				<li><a class="article-share__link" :href="emailLink"><span class="article-share__icon" uk-icon="icon: mail"></span><span>Email</span></a></li>
				<li>
					<a class="article-share__link" href="javascript:void(0)" @click="copyToClipboard">
						<span class="article-share__icon" uk-icon="icon: link"></span>
						<span>{{ copySuccess ? 'Link gekopieerd' : 'Link kopiëren' }}</span>
					</a>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
import { useId } from 'vue';
import Share2 from '@lucide/vue/dist/esm/icons/share-2.mjs';

export default {
	components: {
		Share2,
	},
	setup() {
		return {
			shareToggleId: `article-share-toggle-${useId()}`,
		};
	},
	props: {
		siteName: {
			type: String,
			default: '',
			required: true
		},
		url: {
			type: String,
			required: true
		},
		title: {
			type: String,
			required: true
		},
		mailto: {
			type: String,
			required: true,
			default: 'info@site.com'
		},
		label: {
			type: String,
			required: false
		},
		navBarItem: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data() {
		return {
			copySuccess: false
		};
	},
	computed: {
		emailLink() {
			const subject = encodeURIComponent(`Bekijk dit artikel op ${this.siteName}`);
			const body = encodeURIComponent(`${this.title}:\n${this.url}`);
			return `mailto:${this.mailto}?subject=${subject}&body=${body}`;
		}
	},
	methods: {
		shareOnWhatsApp() {
			var component = this;
			const url = encodeURIComponent(this.url);
			const title = encodeURIComponent(this.title);				
			if (component.$root.device.desktop){
					window.open(`https://api.whatsapp.com/send?text=Bekijk ${title}: ${url}`);
			} else if(component.$root.device.mobile) {
					window.open(`whatsapp://send?text=Bekijk ${title}: ${url}`);
			}
		},
		shareOnFacebook() {
			const url = encodeURIComponent(this.url);
			window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
		},
		shareOnTwitter() {
			const url = encodeURIComponent(this.url);
			const title = encodeURIComponent(this.title);
			window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
		},
		shareOnLinkedIn() {
			const url = encodeURIComponent(this.url);
			const title = encodeURIComponent(this.title);
			window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
		},
		async copyToClipboard() {
			this.copySuccess = false;

			try {
				if (!navigator.clipboard) {
					throw new Error('Clipboard API unavailable');
				}

				await navigator.clipboard.writeText(this.url);
			} catch (error) {
				this.copyToClipboardFallback();
			}

			this.copySuccess = true;
			setTimeout(() => {
				this.copySuccess = false;
			}, 2200);

			UIkit.notification({
				message: 'URL naar je clipboard gekopiëerd',
				status: 'success',
				pos: 'bottom-center'
			});
		},
		copyToClipboardFallback() {
			const urlInput = document.createElement('input');
			urlInput.value = this.url;
			document.body.appendChild(urlInput);
			urlInput.select();
			document.execCommand('copy');
			document.body.removeChild(urlInput);
		}
	}
}
</script>

<style scoped>	
	
.component.article-share {
	display: inline-flex;
	position: relative;
}
.article-share__trigger {
	align-items: center;
	appearance: none;
	background: #f5f6f7;
	border: 1px solid #e1e4e8;
	border-radius: 7px;
	color: #686e76;
	cursor: pointer;
	display: inline-flex;
	font: inherit;
	gap: 7px;
	height: 36px;
	justify-content: center;
	min-width: 36px;
	padding: 0 9px;
	transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}
.article-share__trigger:hover,
.article-share__trigger:focus-visible {
	background: rgba(255, 61, 0, 0.08);
	border-color: rgba(255, 61, 0, 0.28);
	color: var(--tint-color);
	outline: none;
}
.article-share__trigger-icon {
	display: inline-flex;
}
.article-share__trigger-icon svg {
	display: block;
	height: 18px;
	width: 18px;
}
.article-share__label {
	font-weight: 600;
}
.article-share__dropdown {
	background: rgba(255, 255, 255, 0.98);
	border: 1px solid rgba(32, 32, 44, 0.08);
	border-radius: 8px;
	box-shadow: 0 18px 50px rgba(32, 32, 44, 0.18);
	min-width: 250px;
	padding: 8px;
}
.article-share__list {
	list-style: none;
	margin: 0;
	padding: 0;
}
.article-share__link {
	align-items: center;
	border-radius: 7px;
	color: #5e6268;
	display: flex;
	font-size: 0.98rem;
	font-weight: 500;
	gap: 12px;
	min-height: 44px;
	padding: 0 12px;
	text-decoration: none;
	transition: background-color 0.16s ease, color 0.16s ease;
}
.article-share__link:hover,
.article-share__link:focus-visible {
	background: rgba(255, 61, 0, 0.08);
	color: var(--tint-color);
	outline: none;
	text-decoration: none;
}
.article-share__icon {
	align-items: center;
	background: #f3f4f6;
	border-radius: 7px;
	color: #7b7f86;
	display: inline-flex;
	height: 32px;
	justify-content: center;
	width: 32px;
}
.article-share__link:hover .article-share__icon,
.article-share__link:focus-visible .article-share__icon {
	background: rgba(255, 61, 0, 0.12);
	color: var(--tint-color);
}
:global(body.dark .article-share__trigger) {
	background: #242830;
	border-color: #343943;
	color: #b3b9c2;
}
:global(body.dark .article-share__trigger:hover),
:global(body.dark .article-share__trigger:focus-visible) {
	background: rgba(255, 61, 0, 0.12);
	border-color: rgba(255, 61, 0, 0.38);
	color: #ff6333;
}
:global(body.dark .article-share__dropdown) {
	background: #1d2026;
	border-color: #30343d;
}
:global(body.dark .article-share__link) {
	color: #c3c8d0;
}
:global(body.dark .article-share__icon) {
	background: #272b33;
	color: #a7adb6;
}
@media (max-width: 640px) {
	.article-share__dropdown {
		min-width: min(280px, calc(100vw - 32px));
	}
}
</style>
